import { PublicBooking } from "../../../types/service.type"
import {
  getReadablePrimaryButtonStyle,
  resolveOptionalColor,
  resolveThemeColor,
} from "../../../utils/buttonColor"
import { generateBookingMesssage } from "../../../utils/whatsapp"
import { formatDob } from "../../../utils/formatter"
import { saveCustomerQr } from "../../../services/customerQr.service"
import { generateQrDataUrl } from "../../../utils/qrImage"
import moment from "moment"
import { useBookingForm } from "../../../hooks/useBookingForm"
import { Project } from "../../../types/project.type"
import { useEffect, useState, type CSSProperties, type ReactNode } from "react"
import { CalendarPlus, Download, MessageCircle, ShieldAlert } from "lucide-react"
import { useNavigate } from "react-router"
import { Trans, useTranslation } from "react-i18next"

function getCustomFormEntries(booking: PublicBooking): [string, unknown][] {
  const cf = booking.customForm as unknown
  if (!cf) return []
  if (Array.isArray(cf)) {
    return cf.flatMap((item) =>
      item && typeof item === "object" ? Object.entries(item as Record<string, unknown>) : []
    )
  }
  if (typeof cf === "object") {
    return Object.entries(cf as Record<string, unknown>)
  }
  return []
}

export function ConfirmationSection({
  booking,
  project,
}: {
  booking: PublicBooking
  project: Project
}) {
  const { customForms } = useBookingForm(booking.serviceJson, () => {})
  const navigate = useNavigate()
  const { t, i18n } = useTranslation('confirmation')
  const [qrDataUrl, setQrDataUrl] = useState('')
  const [showSaveHint, setShowSaveHint] = useState(false)
  const qrUrl = booking.customerQr?.url

  useEffect(() => {
    if (!qrUrl) return

    let active = true
    generateQrDataUrl(qrUrl)
      .then((dataUrl) => { if (active) setQrDataUrl(dataUrl) })
      .catch((error) => console.error(error))

    return () => { active = false }
  }, [qrUrl])

  const saveQr = async () => {
    if (!qrDataUrl) return

    const outcome = await saveCustomerQr(qrDataUrl, booking.customerQr?.code)
    // Some in-app browsers give us no way to hand a file over, so tell the customer how
    // to save the image they can already see.
    setShowSaveHint(outcome === 'manual')
  }

  const themeColor = resolveThemeColor(project.themeColor)
  const serviceColor = resolveOptionalColor(project.serviceColor)
  const coverUrl = project.coverImageFiles?.[0]?.s3Url

  const backgroundStyles: CSSProperties = coverUrl
    ? {
        backgroundImage: `linear-gradient(to bottom, rgba(15, 23, 42, 0.22) 0%, rgba(15, 23, 42, 0.4) 100%), linear-gradient(135deg, ${themeColor}18 0%, transparent 50%), url(${JSON.stringify(coverUrl)})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }
    : {
        background: `linear-gradient(160deg, ${themeColor} 0%, ${themeColor}dd 45%, ${themeColor}aa 100%)`,
      }

  const waDigits = project.contactNo?.replace(/\D/g, "") ?? ""
  const waHref = waDigits
    ? `https://wa.me/${waDigits}?text=${encodeURIComponent(generateBookingMesssage(booking, project, customForms))}`
    : undefined

  const detailRows: { label: string; value: ReactNode }[] = [
    { label: t('name'), value: booking.customer.name },
    { label: t('phone'), value: booking.customer.phoneNo },
    {
      label: t('location'),
      value: booking.isExternal ? booking.attendedAddress : project.offsiteName,
    },
    {
      label: t('services'),
      value: booking.serviceJson.map((s) => s.title).join(", "),
    },
    {
      label: t('dateTime'),
      value: (
        <>
          {moment(booking.reservationDateTime).locale(i18n.language?.startsWith('id') ? 'id' : 'en').format("DD MMM YYYY, HH:mm")}
          <p className="mt-2 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-left text-xs font-normal leading-relaxed text-amber-950">
            {t('timeNote')}
          </p>
        </>
      ),
    },
    { label: t('notes'), value: booking.remark?.trim() ? booking.remark : "—" },
  ]

  getCustomFormEntries(booking).forEach(([key, value]) => {
    const formDef = customForms?.find((f) => f.key === key)
    const displayValue =
      formDef?.type === 'dob'
        ? formatDob(value as string | Date | null | undefined)
        : String(value)
    detailRows.push({
      label: `${formDef?.label ?? key}`,
      value: displayValue,
    })
  })

  return (
    <div className="relative min-h-screen" style={backgroundStyles}>
      <div
        className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/12 to-transparent md:from-black/18"
        aria-hidden
      />

      <div className="relative z-[1] mx-auto flex max-w-3xl flex-col gap-6 px-5 pb-16 pt-10 md:pb-24 md:pt-14">
        <section
          className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white/95 text-gray-900 shadow-xl shadow-gray-900/10 backdrop-blur-sm"
          style={{
            boxShadow: `0 25px 50px -12px rgba(0, 0, 0, 0.12), 0 0 0 1px ${themeColor}12`,
          }}
        >
          <div
            className="border-b border-gray-100 px-6 py-6 text-center sm:px-8 sm:py-8"
            style={{
              background: `linear-gradient(135deg, ${themeColor}0d 0%, transparent 55%), linear-gradient(to bottom, #fafafa, #ffffff)`,
            }}
          >
            <p
              className="text-xs font-semibold uppercase tracking-wider"
              style={{ color: themeColor }}
            >
              {t('badge')}
            </p>
            <h1 className="mt-2 text-2xl font-semibold tracking-tight text-gray-900 md:text-3xl">
              {t('thanks')}
            </h1>
            <p className="mx-auto mt-3 max-w-lg text-sm leading-relaxed text-gray-600">
              <Trans
                i18nKey="confirmation:body"
                components={{
                  screenshot: <span className="font-medium text-gray-800" />,
                }}
              />
            </p>
          </div>

          <div
            className="p-6 sm:p-8"
            style={
              serviceColor
                ? {
                    background: `linear-gradient(180deg, ${serviceColor}08 0%, #ffffff 35%)`,
                  }
                : undefined
            }
          >
            <dl className="divide-y divide-gray-100">
              {detailRows.map((row, i) => (
                <div
                  key={i}
                  className="grid gap-1 py-4 first:pt-0 sm:grid-cols-[minmax(0,10rem)_1fr] sm:gap-6 sm:py-5"
                >
                  <dt className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {row.label}
                  </dt>
                  <dd className="text-sm font-medium text-gray-900">{row.value}</dd>
                </div>
              ))}
            </dl>

            {waHref ? (
              <button
                type="button"
                className="mt-8 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95"
                style={getReadablePrimaryButtonStyle(themeColor)}
                onClick={() => window.open(waHref, "_blank", "noopener,noreferrer")}
              >
                <MessageCircle className="h-5 w-5 shrink-0" aria-hidden />
                {t('openWhatsApp', { phone: project.contactNo })}
              </button>
            ) : (
              <p className="mt-6 text-center text-sm text-gray-500">
                {t('whatsappNotConfigured')}
              </p>
            )}

            <button
              type="button"
              className="mt-3 flex w-full items-center justify-center gap-2 rounded-xl border px-5 py-3.5 text-sm font-semibold transition hover:bg-gray-50 active:brightness-95"
              style={{ borderColor: themeColor, color: themeColor }}
              onClick={() => navigate("/booking")}
            >
              <CalendarPlus className="h-5 w-5 shrink-0" aria-hidden />
              {t('bookAgain')}
            </button>
          </div>
        </section>

        {qrUrl ? (
          <section className="overflow-hidden rounded-2xl border border-gray-200/90 bg-white/95 p-6 text-center text-gray-900 shadow-xl shadow-gray-900/10 backdrop-blur-sm sm:p-8">
            <h2 className="text-lg font-semibold tracking-tight text-gray-900">{t('qr.title')}</h2>
            <p className="mx-auto mt-2 max-w-md text-sm leading-relaxed text-gray-600">{t('qr.hint')}</p>

            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt={t('qr.title')}
                className="mx-auto mt-5 h-56 w-56 rounded-xl border border-gray-100 bg-white p-2"
              />
            ) : (
              <div className="mx-auto mt-5 h-56 w-56 animate-pulse rounded-xl bg-gray-100" />
            )}

            <button
              type="button"
              disabled={!qrDataUrl}
              className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl px-5 py-3.5 text-sm font-semibold shadow-md transition hover:brightness-105 active:brightness-95 disabled:opacity-60"
              style={getReadablePrimaryButtonStyle(themeColor)}
              onClick={saveQr}
            >
              <Download className="h-5 w-5 shrink-0" aria-hidden />
              {t('qr.save')}
            </button>

            {showSaveHint ? (
              <p className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-left text-xs leading-relaxed text-gray-700">
                {t('qr.saveHint')}
              </p>
            ) : null}

            <p className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200/90 bg-amber-50 px-3 py-2 text-left text-xs leading-relaxed text-amber-950">
              <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
              {t('qr.warning')}
            </p>
          </section>
        ) : null}
      </div>
    </div>
  )
}
